import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { StatusBadge } from "../../../constants/statusConstants";
import { AuthContext } from "../../../contexts/AuthContext";
import { customerProfileService } from "../../../services/api/customerProfileService";
import { formatDate } from "../../../utils/DateFormat";
import CustomTable from "../components/CustomTable";
import Loading from "../components/Loading";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import { EstimatePopup } from "../quan-ly-dich-vu/components/popup/EstimatePopup";

const columns = [
  {
    key: "id",
    title: "STT",
    minWidth: "20px",
    align: "text-left",
    render: (value) => <div className="pl-2">{value}</div>,
  },

  {
    key: "type",
    title: "Loại",
    minWidth: "180px",
    align: "text-center",
    render: (value) => {
      const typeMap = {
        profile: "Hồ sơ",
        estimate: "Báo giá",
      };

      return (
        <div className="flex items-center justify-center">
          {typeMap[value] || value}
        </div>
      );
    },
  },
  {
    key: "soDon",
    title: "Số đơn",
    minWidth: "120px",
    color: "text-[#0954E9]",
  },
  {
    key: "note",
    title: "Ghi chú",
    minWidth: "200px",
    render: (value) =>
      value ? (
        <span className="text-gray-700 line-clamp-2" title={value}>
          {value}
        </span>
      ) : (
        "--"
      ),
  },
  {
    key: "nguoiDeXuat",
    title: "Người đề xuất",
    minWidth: "150px",
  },
  {
    key: "ngayDeXuat",
    title: "Ngày đề xuất",
    minWidth: "150px",
  },
  {
    key: "nguoiDuyet",
    title: "Người duyệt",
    minWidth: "150px",
  },
  {
    key: "ngayDuyet",
    title: "Ngày duyệt",
    minWidth: "150px",
  },
  {
    key: "trangThai",
    title: "Trạng thái",
    minWidth: "120px",
    render: (value) => <StatusBadge status={value} />,
  },
];

const ListProposalsForApproval = () => {
  const { token } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isEstimateOpen, setIsEstimateOpen] = useState(false);
  const [estimateData, setEstimateData] = useState(null);

  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false); // Loading cho action (fetch estimate, update)
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("fromDate") || "");
  const [toDate, setToDate] = useState(searchParams.get("toDate") || "");

  const currentPage = Number(searchParams.get("page")) || 1;

  const convertDateForAPI = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  const fetchData = useCallback(async () => {
    try {
      setTableLoading(true);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      const urlSearch = searchParams.get("search");
      const urlFromDate = searchParams.get("fromDate");
      const urlToDate = searchParams.get("toDate");

      if (urlSearch) params.search_value = urlSearch;
      if (urlFromDate) params.fromDate = convertDateForAPI(urlFromDate);
      if (urlToDate) params.toDate = convertDateForAPI(urlToDate);

      const response = await customerProfileService.get_list_request_profile(token, params);

      if (!response?.data) {
        setData([]);
        setTotalPages(1);
        return;
      }

      const formatted = response.data.map((item, index) => ({
        id: itemsPerPage * (currentPage - 1) + index + 1,
        mau: "",
        ten: item.profileCode || "--",
        soDon: item.profileCode || "--",
        nguoiDeXuat: item.requestedBy || "--",
        ngayDeXuat: formatDate(item.createdAt),
        nguoiDuyet: item.approvedBy || "--",
        ngayDuyet: formatDate(item.approvedAt),
        trangThai: item.status || "Chờ duyệt",
        note: item.note || "",
        nav_id: item.profileId,
        type: item.type,
        profileId: item.profileId,
        estimateId: item.estimateId,
      }));

      setData(formatted);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.dismiss();
      toast.error(error.response?.data?.message || "Không thể tải danh sách đề xuất");
      setData([]);
      setTotalPages(1);
    } finally {
      setTableLoading(false);
    }
  }, [currentPage, itemsPerPage, token, searchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchApply = () => {
    const newParams = new URLSearchParams();
    if (search) newParams.set("search", search);
    if (fromDate) newParams.set("fromDate", fromDate);
    if (toDate) newParams.set("toDate", toDate);
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleFromDateChange = (newFromDate) => {
    if (!toDate) {
      setFromDate(newFromDate);
      return;
    }
    const to = toDate ? new Date(toDate.split("-").reverse().join("-")) : null;
    const from = new Date(newFromDate.split("-").reverse().join("-"));

    if (to && from > to) {
      alert("⚠️ Ngày bắt đầu không được lớn hơn ngày kết thúc.");
      return;
    }

    setFromDate(newFromDate);
  };

  const handleToDateChange = (newToDate) => {
    if (!fromDate) {
      setToDate(newToDate);
      return;
    }

    const from = fromDate
      ? new Date(fromDate.split("-").reverse().join("-"))
      : null;
    const to = new Date(newToDate.split("-").reverse().join("-"));

    if (from && to < from) {
      alert("⚠️ Ngày kết thúc không được nhỏ hơn ngày bắt đầu.");
      return;
    }

    setToDate(newToDate);
  };

  const handleRowClick = async (row) => {
    if (row.type === "profile") {
      navigate(`/quan-ly-dich-vu-khach-hang/ho-so/${row.profileId}`);
      return;
    }

    if (row.type === "estimate") {
      try {
        setActionLoading(true);
        const res = await customerProfileService.get_profile_estimate(
          token,
          row.profileId,
        );
        setEstimateData(res.data);
        setIsEstimateOpen(true);
      } catch (err) {
        toast.error("Không thể lấy báo phí");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleUpdateEstimate = async (estimateId, payload) => {
    try {
      setActionLoading(true);

      const res = await customerProfileService.update_profile_estimate(
        token,
        estimateId,
        payload,
      );

      toast.success("Cập nhật báo phí thành công");

      setEstimateData({
        ...res.data,
        _id: res.data._id || res.data.estimateId || estimateId,
      });

      await fetchData();

      return res;
    } catch (err) {
      toast.error(err.response?.data?.message || "Cập nhật báo phí thất bại");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col gap-4 bg-gray-50">
      {/* Loading overlay cho actions */}
      {actionLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <Loading />
            <p className="mt-4 text-gray-700">Đang xử lý...</p>
          </div>
        </div>
      )}

      <div className="shrink-0 px-1">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Danh sách chờ duyệt
        </h1>
        <p className="text-gray-600">
          Các hồ sơ có trạng thái "Chờ duyệt" và đã được đề xuất duyệt
        </p>
      </div>

      <div className="shrink-0">
        <SearchAndFilterBar
          searchPlaceholder="Tìm kiếm hồ sơ chờ duyệt"
          searchValue={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          onSearchClick={handleSearchApply}
          showDatePickers
          showFilter
          filterLabel="Áp dụng"
          fromDatePlaceholder="Từ ngày"
          fromDateValue={fromDate}
          toDatePlaceholder="Đến ngày"
          toDateValue={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          showAdditionBtn={false}
        />
      </div>

      <div className="min-h-0 flex-1">
        <CustomTable
          data={data}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={(page) => {
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              params.set("page", page.toString());
              return params;
            });
          }}
          totalPages={totalPages}
          setView
          setEdit={false}
          setDuplicate={false}
          setDelete={false}
          onRowClick={handleRowClick}
          loading={tableLoading}
          loadingText="Đang tải danh sách đề xuất..."
        />
      </div>

      {isEstimateOpen && (
        <EstimatePopup
          isOpen={isEstimateOpen}
          onClose={() => {
            setIsEstimateOpen(false);
            setEstimateData(null);
          }}
          data={estimateData}
          getAdjustmentHistory={
            customerProfileService.get_estimate_adjustment_history
          }
          getPendingAdjustment={customerProfileService.get_pending_adjustment}
          updatePendingAdjustment={customerProfileService.update_pending_adjustment}
          onUpdate={handleUpdateEstimate}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default ListProposalsForApproval;
